import { useRef } from "react";
import {
  motion,
  useMotionValue,
  useScroll,
  useSpring,
  useTransform,
} from "framer-motion";
import { ArrowRight, PhoneCall, Send, Users } from "lucide-react";
import { EASE } from "@/components/Reveal";
import { CALENDLY_URL, scrollToSection } from "@/lib/scroll";

const HERO_IMAGE = "/abhi-portrait.png";

const HEADLINE_LINES = [
  <>Replace random networking</>,
  <>with a structured outbound</>,
  <>pipeline, built and</>,
  <>
    tested in <em className="italic text-terra">7 days.</em>
  </>,
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 70]);
  const floatY = useTransform(scrollYProgress, [0, 1], [0, -55]);

  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [5, -5]), {
    stiffness: 120,
    damping: 16,
  });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), {
    stiffness: 120,
    damping: 16,
  });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const onLeave = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <section ref={ref} className="overflow-hidden pt-28 pb-16 lg:pt-36 lg:pb-20">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-12">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="inline-flex items-center gap-2 rounded-full border border-terra/20 bg-terra-soft px-4 py-1.5"
            data-testid="hero-eyebrow"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-terra" />
            <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-terra">
              7-Day Outbound Sprint · For B2B Service Founders
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="mt-6 font-serif text-[clamp(1.9rem,4.4vw,3.7rem)] font-semibold leading-[1.06] tracking-tight text-ink"
          >
            {HEADLINE_LINES.map((line, i) => (
              <span key={i} className="-mb-[0.12em] block overflow-hidden pb-[0.12em]">
                <motion.span
                  className="block"
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.95, ease: EASE, delay: 0.18 + i * 0.1 }}
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.div
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.75 }}
            className="mt-7 h-px w-24 origin-left bg-terra"
          />

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.65 }}
            className="mt-7 max-w-xl text-base leading-relaxed text-smoke sm:text-lg"
            data-testid="hero-subheadline"
          >
            For B2B service founders. Get 50 researched target accounts, the right
            decision-makers, ready-to-send messaging, and a live test of email,
            LinkedIn and calling, so you start next week with conversations instead
            of a blank page.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: 0.8 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-cta-button"
              className="inline-flex items-center gap-2 rounded-full bg-terra px-8 py-4 text-base font-medium tracking-wide text-white shadow-md transition-all duration-300 hover:-translate-y-0.5 hover:bg-terra-dark"
            >
              Book a 15-minute fit call
              <ArrowRight className="h-4 w-4" />
            </a>
            <button
              type="button"
              onClick={() => scrollToSection("#process")}
              data-testid="hero-secondary-cta"
              className="inline-flex items-center gap-2 rounded-full border border-ink bg-cream px-7 py-3.5 text-base font-medium text-ink transition-all duration-300 hover:bg-ink hover:text-cream"
            >
              See the 7-day plan
            </button>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-7 flex items-center gap-2.5 font-mono text-[11px] font-medium uppercase tracking-[0.16em] text-smoke sm:text-xs"
            data-testid="hero-trust-line"
          >
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-terra opacity-60" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-terra" />
            </span>
            Pilot price for the first 3 founders only:{" "}
            <span className="font-semibold text-terra">$149</span>
          </motion.p>
        </div>

        <motion.div
          style={{ y: imgY }}
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, ease: EASE, delay: 0.45 }}
          className="relative lg:col-span-5"
          data-testid="hero-visual-card"
        >
          <div className="absolute -right-10 -top-10 h-72 w-72 rounded-full bg-terra/15 blur-3xl" />
          <div className="absolute -bottom-8 -left-8 h-56 w-56 rounded-full bg-gold/20 blur-3xl" />

          <div className="relative [perspective:1400px]">
            <motion.div
              onMouseMove={onMove}
              onMouseLeave={onLeave}
              style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
              className="relative"
            >
              <div className="overflow-hidden rounded-[2rem] rounded-t-[10rem] border border-line bg-sand shadow-[0_40px_80px_-30px_rgba(26,26,25,0.35)]">
                <img
                  src={HERO_IMAGE}
                  alt="Abhi, founder of Sell With Abhi"
                  className="h-[440px] w-full object-cover sm:h-[520px] lg:h-[560px]"
                />
                <div className="pointer-events-none absolute inset-0 bg-terra/10 mix-blend-multiply" />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
              </div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 1 }}
                style={{ y: floatY }}
                className="absolute -left-3 top-16 hidden items-center gap-3 rounded-2xl border border-line bg-white/90 px-5 py-4 shadow-xl backdrop-blur sm:flex [transform:translateZ(70px)]"
                data-testid="hero-float-accounts"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-terra-soft">
                  <Users className="h-5 w-5 text-terra" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-smokeLight">
                    Researched
                  </span>
                  <span className="block text-sm font-semibold text-ink">
                    50 target accounts
                  </span>
                </span>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease: EASE, delay: 1.15 }}
                style={{ y: floatY }}
                className="absolute -right-3 bottom-12 hidden items-center gap-3 rounded-2xl border border-line bg-white/90 px-5 py-4 shadow-xl backdrop-blur sm:flex [transform:translateZ(70px)]"
                data-testid="hero-float-proof"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-terra-soft">
                  <Send className="h-5 w-5 text-terra" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.16em] text-smokeLight">
                    Live proof run
                  </span>
                  <span className="block text-sm font-semibold text-ink">
                    First touches sent for you
                  </span>
                </span>
              </motion.div>
            </motion.div>
          </div>

          <div className="mt-6 flex items-center justify-end gap-2 pr-2 font-mono text-[11px] uppercase tracking-[0.16em] text-smokeLight">
            <PhoneCall className="h-3.5 w-3.5 text-terra" />
            Day 6 — test calls made for you
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
